import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  Car, 
  Plane, 
  Heart, 
  Users, 
  Zap, 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  ShieldCheck, 
  Leaf,
  Menu,
  X,
  ChevronRight,
  Send,
  MessageCircle,
  Facebook
} from "lucide-react";

// Company Info
const COMPANY = {
  name: "Taxi Điện Hiếu Cao",
  phone: "0833332477",
  zalo: "0833332477",
  email: "support@taxidienhc.vn",
  address: "Số 245, Đường DT943, Phường Long Xuyên, Tỉnh An Giang"
};

// Services Data
const SERVICES = [
  {
    id: 1,
    icon: Zap,
    title: "Xe Taxi Dịch Vụ",
    description: "Dịch vụ taxi điện thân thiện môi trường, tiết kiệm chi phí, vận hành êm ái. Di chuyển trong thành phố Long Xuyên nhanh chóng và tiện lợi.",
    features: ["Không khí thải", "Giá cả hợp lý", "Đặt xe 24/7"],
    image: "/taxi.png"
  },
  {
    id: 2,
    icon: Plane,
    title: "Xe Đưa Đón Sân Bay",
    description: "Dịch vụ đưa đón sân bay Cần Thơ, Tân Sơn Nhất đúng giờ, an toàn. Theo dõi chuyến bay và hỗ trợ hành lý.",
    features: ["Theo dõi chuyến bay", "Hỗ trợ hành lý", "Đúng giờ cam kết"],
    image: "taxi-airpot.png"
  },
  {
    id: 3,
    icon: Car,
    title: "Xe Du Lịch Đường Dài",
    description: "Thuê xe du lịch đường dài với tài xế kinh nghiệm. Khám phá miền Tây sông nước An Giang, Cần Thơ và các tỉnh lân cận.",
    features: ["Tài xế địa phương", "Xe đời mới", "Linh hoạt lộ trình"],
    image: "/taxi-road.png"
  },
  {
    id: 4,
    icon: Heart,
    title: "Xe Dịch Vụ",
    description: "Dịch vụ xe cưới sang trọng, trang trí đẹp mắt. Mang đến ngày trọng đại của bạn thêm phần hoàn hảo.",
    features: ["Trang trí hoa", "Xe sang trọng", "Lái xe chuyên nghiệp"],
    image: "/taxi-service.png"
  },
  {
    id: 5,
    icon: Users,
    title: "Xe Hợp Đồng Theo Tour",
    description: "Dịch vụ cho thuê xe theo tour, hợp đồng dài hạn cho các công ty du lịch, đoàn khách. Đa dạng loại xe từ 4 đến 45 chỗ.",
    features: ["Xe 4-45 chỗ", "Hợp đồng linh hoạt", "Giá ưu đãi đoàn"],
    image: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?q=80&w=800&auto=format&fit=crop"
  }
];

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "/#home", label: "Trang chủ" },
    { href: "/#services", label: "Dịch vụ" },
    { href: "/#pricing", label: "Bảng giá" },
    { href: "/#contact", label: "Liên hệ" }
  ];

  const isHomePage = location.pathname === '/';

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'nav-scrolled py-3' : 'bg-transparent py-5'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="logo-link"
          >
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform shadow-md">
              <img 
                src="/logo.png" 
                alt="Taxi Điện Hiếu Cao Logo" 
                className="w-8 h-8 object-contain"
              />
            </div>
            <div>
              <span className="font-heading font-bold text-xl text-slate-900">Hiếu Cao</span>
              <p className="text-xs text-slate-500">Taxi Điện</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-slate-700 hover:text-cyan-600 font-medium transition-colors"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </a>
            ))}
            <a href={`tel:${COMPANY.phone}`} onClick={() => gtag_report_conversion(`tel:${COMPANY.phone}`)}>
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6 shadow-lg shadow-red-500/25"
                data-testid="nav-call-button"
              >
                <Phone className="w-4 h-4 mr-2" />
                Gọi ngay
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 py-4 bg-white rounded-2xl shadow-lg mobile-menu-enter">
            <div className="flex flex-col gap-2 px-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="py-3 px-4 text-slate-700 hover:bg-slate-50 rounded-xl font-medium transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </a>
              ))}
              <a href={`tel:${COMPANY.phone}`} onClick={() => gtag_report_conversion(`tel:${COMPANY.phone}`)}>
                <Button 
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full"
                  data-testid="mobile-call-button"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {COMPANY.phone}
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="home" 
      className="min-h-screen flex items-center pt-20 relative"
      style={{
        backgroundImage: 'url("/hero.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
      data-testid="hero-section"
    >
      {/* Overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-transparent backdrop-blur-[4px]"></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 relative z-10">
        <div className="max-w-2xl">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-yellow-100 text-emerald-600 px-4 py-2 rounded-full text-sm font-medium">
              <Leaf className="w-4 h-4" />
              Xe điện thân thiện môi trường
            </div>
            
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white leading-tight tracking-tight drop-shadow-lg">
              Dịch vụ du lịch <br />
              <span className="text-cyan-200 drop-shadow-lg">Taxi Điện HC</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white leading-relaxed drop-shadow-md">
              Taxi Điện HC - Đơn vị cung cấp dịch vụ vận chuyển hành khách uy tín hàng đầu tại An Giang.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a href={`tel:${COMPANY.phone}`} onClick={() => gtag_report_conversion(`tel:${COMPANY.phone}`)}>
                <Button 
                  size="lg" 
                  className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-red-500/25 hover:scale-105 transition-all"
                  data-testid="hero-phone-button"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {COMPANY.phone}
                </Button>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-6 pt-4">
              <div className="flex items-center gap-2 text-white/80">
                <ShieldCheck className="w-5 h-5 text-cyan-400" />
                <span className="text-sm font-medium">An toàn 100%</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Clock className="w-5 h-5 text-cyan-400" />
                <span className="text-sm font-medium">Hoạt động 24/7</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Zap className="w-5 h-5 text-emerald-400" />
                <span className="text-sm font-medium">Xe điện xanh</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const bentoClasses = [
    "bento-item-1",
    "bento-item-2", 
    "bento-item-3",
    "bento-item-4",
    "bento-item-5"
  ];

  return (
    <section 
      id="services" 
      className="py-24 md:py-32 bg-slate-50"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sm text-cyan-600 font-semibold uppercase tracking-widest">
            Dịch vụ của chúng tôi
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-4 tracking-tight">
            Đa dạng loại hình vận chuyển
          </h2>
          <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
            Từ taxi điện nội thành đến xe du lịch đường dài, chúng tôi đáp ứng mọi nhu cầu di chuyển của bạn
          </p>
        </div>

        {/* Services Grid - Bento Style */}
        <div className="bento-grid grid gap-6">
          {SERVICES.map((service, index) => (
            <Card 
              key={service.id}
              className={`service-card bg-white border-slate-100 overflow-hidden group cursor-pointer ${bentoClasses[index]}`}
              data-testid={`service-card-${service.id}`}
            >
              <CardContent className="p-0">
                <div className={`${index === 1 || index === 4 ? 'md:flex' : ''}`}>
                  {/* Image */}
                  <div className={`relative overflow-hidden ${index === 1 || index === 4 ? 'md:w-1/2' : 'h-48'}`}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        <service.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className={`p-6 ${index === 1 || index === 4 ? 'md:w-1/2 md:flex md:flex-col md:justify-center' : ''}`}>
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 mb-4 leading-relaxed">
                      {service.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((feature, idx) => (
                        <span 
                          key={idx}
                          className="text-xs bg-cyan-50 text-cyan-700 px-3 py-1 rounded-full font-medium"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const pricingData = [
    { from: "Long Xuyên", to: "Cần Thơ", price: "800k", note: "" },
    { from: "Long Xuyên", to: "Sân bay Cần Thơ", price: "700k", note: "" },
    { from: "Long Xuyên", to: "Rạch Giá", price: "800k", note: "" },
    { from: "Long Xuyên", to: "Châu Đốc", price: "600k", note: "" },
    { from: "Long Xuyên", to: "An Phú", price: "700k", note: "" },
    { from: "Long Xuyên", to: "Cửa Khẩu Long Bình", price: "900k", note: "" },
    { from: "Long Xuyên", to: "Tịnh Biên", price: "800k", note: "" },
    { from: "Long Xuyên", to: "Cao Lãnh", price: "500k", note: "" },
    { from: "Long Xuyên", to: "Sa Đéc", price: "550k", note: "" },
    { from: "Long Xuyên", to: "Hà Tiên", price: "1tr300k", note: "" },
    { from: "Long Xuyên", to: "Sóc Trăng", price: "1tr300k", note: "" },
    { from: "Long Xuyên", to: "Trần Đề", price: "1tr500k", note: "" },
    { from: "Long Xuyên", to: "Hậu Giang", price: "1tr000k", note: "" },
    { from: "Long Xuyên", to: "Vị Thanh", price: "900k", note: "" },
    { from: "Long Xuyên", to: "Bạc Liêu", price: "1tr700k", note: "" },
    { from: "Long Xuyên", to: "Bến Tre", price: "1tr500k", note: "" },
    { from: "Long Xuyên", to: "Cà Mau", price: "1tr800k", note: "" },
    { from: "Long Xuyên", to: "Sân bay TSN", price: "1tr800k", note: "" },
    { from: "Long Xuyên", to: "Bến tàu Rạch Giá", price: "800k", note: "" }
  ];

  return (
    <section 
      id="pricing" 
      className="py-24 md:py-32 bg-slate-50"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sm text-cyan-600 font-semibold uppercase tracking-widest">
            Bảng giá
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-4 tracking-tight">
            Giá cả cạnh tranh
          </h2>
          <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
            (Đây là bảng giá tham khảo, vui lòng liên hệ để được báo giá chính xác nhất)
          </p>
        </div>

        {/* Hotline Info */}
        <div className="bg-red-600 text-white rounded-2xl p-6 mb-12 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-2xl">☎️</span>
            <span className="text-xl font-bold">Hotline: 0833332477</span>
          </div>
          <p className="text-sm opacity-90">Liên hệ ngay để được tư vấn và đặt xe</p>
        </div>

        {/* Pricing Info */}
        <div className="bg-white rounded-2xl p-8 mb-12 shadow-lg">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🚘</span>
              <div>
                <p className="font-semibold text-slate-900">Nhận hợp đồng đi các tỉnh miền Tây</p>
                <p className="text-slate-600">1 chiều giá chỉ 12k/km</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl">🚕</span>
              <div>
                <p className="font-semibold text-slate-900">2 chiều giá chỉ 8k/km</p>
                <p className="text-slate-600">Tiết kiệm chi phí cho chuyến đi khứ hồi</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-4 bg-cyan-50 rounded-xl">
            <span className="text-2xl">☑️</span>
            <p className="font-medium text-cyan-900">
              Chuyên cho Thuê Xe 4-7-16-29 chỗ (Tự Lái và Có Tài)
            </p>
          </div>
        </div>

        {/* Pricing Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cyan-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">STT</th>
                  <th className="px-6 py-4 text-left font-semibold">Từ</th>
                  <th className="px-6 py-4 text-left font-semibold">Đến</th>
                  <th className="px-6 py-4 text-right font-semibold">Giá</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {pricingData.map((item, index) => (
                  <tr key={index} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-slate-600 font-medium">{index + 1}</td>
                    <td className="px-6 py-4 text-slate-900">{item.from}</td>
                    <td className="px-6 py-4 text-slate-900">{item.to}</td>
                    <td className="px-6 py-4 text-right font-semibold text-cyan-600">{item.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Reference Pricing */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-emerald-500 to-cyan-600 text-white p-6">
            <h3 className="font-heading text-2xl font-bold text-center">Bảng giá tham khảo</h3>
            <p className="text-center opacity-90 mt-2">Giá theo kilometer cho các tuyến đường khác</p>
          </div>
          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border-2 border-emerald-200">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🚘</span>
                    <div>
                      <p className="font-semibold text-emerald-900">Giá 1 chiều</p>
                      <p className="text-sm text-emerald-700">Các tỉnh miền Tây</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-emerald-600">12k/km</div>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Loại xe cho thuê:</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                      <span className="text-slate-700">Xe 4 chỗ</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                      <span className="text-slate-700">Xe 7 chỗ</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                      <span className="text-slate-700">Xe 16 chỗ</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>
                      <span className="text-slate-700">Xe 29 chỗ</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-cyan-50 rounded-xl border-2 border-cyan-200">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🚕</span>
                    <div>
                      <p className="font-semibold text-cyan-900">Giá 2 chiều</p>
                      <p className="text-sm text-cyan-700">Khứ hồi tiết kiệm</p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-cyan-600">8k/km</div>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="font-semibold text-slate-900 mb-3">Hình thức dịch vụ:</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-emerald-600 rounded-full"></span>
                      <span className="text-slate-700">Tự lái</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-emerald-600 rounded-full"></span>
                      <span className="text-slate-700">Có tài xế</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-emerald-600 rounded-full"></span>
                      <span className="text-slate-700">Hợp đồng dài hạn</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-emerald-600 rounded-full"></span>
                      <span className="text-slate-700">Tour du lịch</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
              <div className="flex items-start gap-3">
                <span className="text-xl">💡</span>
                <div>
                  <p className="font-semibold text-yellow-900 mb-1">Lưu ý quan trọng:</p>
                  <p className="text-sm text-yellow-800">
                    Giá trên đã bao gồm phí đường bộ, cầu phí. Giá có thể thay đổi tùy theo thời điểm và yêu cầu đặc biệt.
                    Liên hệ hotline để được báo giá chính xác nhất!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center mt-12">
          <a href={`tel:${COMPANY.phone}`} onClick={() => gtag_report_conversion(`tel:${COMPANY.phone}`)}>
            <Button 
              size="lg" 
              className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-red-500/25 hover:scale-105 transition-all"
            >
              <Phone className="w-5 h-5 mr-2" />
              Gọi ngay đặt xe
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      toast.error("Vui lòng điền họ tên và số điện thoại");
      return;
    }
    
    // Construct Zalo message
    const message = `Xin chào! Tôi là ${formData.name}. SĐT: ${formData.phone}. ${formData.message}`;
    const zaloUrl = `https://zalo.me/${COMPANY.zalo}?text=${encodeURIComponent(message)}`;
    
    toast.success("Đang chuyển hướng đến Zalo...");
    window.open(zaloUrl, '_blank');
    
    // Reset form
    setFormData({ name: '', phone: '', email: '', message: '' });
  };

  return (
    <section 
      id="contact" 
      className="py-24 md:py-32 bg-white"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <span className="text-sm text-cyan-600 font-semibold uppercase tracking-widest">
                Liên hệ với chúng tôi
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-4 tracking-tight">
                Đặt xe ngay hôm nay
              </h2>
              <p className="text-lg text-slate-600 mt-4">
                Liên hệ ngay với Taxi Điện Hiếu Cao để được tư vấn và báo giá tốt nhất
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <a 
                href={`tel:${COMPANY.phone}`}
                onClick={() => gtag_report_conversion(`tel:${COMPANY.phone}`)}
                className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl hover:bg-cyan-50 transition-colors group"
                data-testid="contact-phone-link"
              >
                <div className="w-14 h-14 bg-cyan-600 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Điện thoại</p>
                  <p className="text-lg font-semibold text-slate-900">{COMPANY.phone}</p>
                </div>
              </a>

              <a 
                href={`https://zalo.me/${COMPANY.zalo}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl hover:bg-blue-50 transition-colors group"
                data-testid="contact-zalo-link"
              >
                <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                  <img 
                    src="/zalo.svg" 
                    alt="Zalo" 
                    className="w-6 h-6"
                  />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Zalo</p>
                  <p className="text-lg font-semibold text-slate-900">{COMPANY.zalo}</p>
                </div>
              </a>

              <a 
                href={`mailto:${COMPANY.email}`}
                className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors group"
                data-testid="contact-email-link"
              >
                <div className="w-14 h-14 bg-slate-700 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="text-lg font-semibold text-slate-900">{COMPANY.email}</p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl">
                <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Địa chỉ</p>
                  <p className="text-base font-medium text-slate-900">{COMPANY.address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-50 p-8 md:p-10 rounded-3xl">
            <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">
              Gửi yêu cầu đặt xe
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Họ và tên <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-white border-slate-200 rounded-xl h-12 contact-input"
                  data-testid="contact-name-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Số điện thoại <span className="text-red-500">*</span>
                </label>
                <Input
                  type="tel"
                  placeholder="0912 345 678"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-white border-slate-200 rounded-xl h-12 contact-input"
                  data-testid="contact-phone-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Email (không bắt buộc)
                </label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-white border-slate-200 rounded-xl h-12 contact-input"
                  data-testid="contact-email-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Nội dung yêu cầu
                </label>
                <Textarea
                  placeholder="Ví dụ: Tôi cần đặt xe đi sân bay Cần Thơ vào ngày 15/01..."
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="bg-white border-slate-200 rounded-xl min-h-[120px] contact-input"
                  data-testid="contact-message-input"
                />
              </div>

              <Button 
                type="submit"
                size="lg"
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full py-6 text-lg font-semibold shadow-lg shadow-cyan-500/25 hover:scale-[1.02] transition-all"
                data-testid="contact-submit-button"
              >
                <Send className="w-5 h-5 mr-2" />
                Gửi yêu cầu qua Zalo
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-md">
                <img 
                  src="/logo.png" 
                  alt="Taxi Điện Hiếu Cao Logo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div>
                <span className="font-heading font-bold text-xl">Hiếu Cao</span>
                <p className="text-xs text-slate-400">Taxi Điện</p>
              </div>
            </div>
            <p className="text-slate-400 mb-6 max-w-md">
              Taxi Điện Hiếu Cao - Đơn vị vận chuyển hành khách uy tín tại Long Xuyên, An Giang. 
              Cam kết an toàn, đúng giờ, giá cả hợp lý.
            </p>
            <div className="flex gap-4">
              <a 
                href={`tel:${COMPANY.phone}`}
                onClick={() => gtag_report_conversion(`tel:${COMPANY.phone}`)}
                className="w-10 h-10 bg-slate-800 hover:bg-cyan-600 rounded-full flex items-center justify-center transition-colors"
                data-testid="footer-phone-icon"
                aria-label="Gọi điện"
              >
                <Phone className="w-5 h-5" />
              </a>
              <a 
                href={`https://zalo.me/${COMPANY.zalo}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
                data-testid="footer-zalo-icon"
                aria-label="Liên hệ Zalo"
              >
                <img 
                  src="/zalo.svg" 
                  alt="Zalo" 
                  className="w-5 h-5"
                />
              </a>
              <a 
                href={`mailto:${COMPANY.email}`}
                className="w-10 h-10 bg-slate-800 hover:bg-slate-700 rounded-full flex items-center justify-center transition-colors"
                data-testid="footer-email-icon"
                aria-label="Gửi email"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Liên kết</h4>
            <ul className="space-y-3">
              <li>
                <a href="/#home" className="text-slate-400 hover:text-cyan-400 transition-colors" data-testid="footer-link-home">
                  Trang chủ
                </a>
              </li>
              <li>
                <a href="/#services" className="text-slate-400 hover:text-cyan-400 transition-colors" data-testid="footer-link-services">
                  Dịch vụ
                </a>
              </li>
              <li>
                <a href="/#contact" className="text-slate-400 hover:text-cyan-400 transition-colors" data-testid="footer-link-contact">
                  Liên hệ
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Pháp lý</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/policy-privacy" className="text-slate-400 hover:text-cyan-400 transition-colors" data-testid="footer-link-privacy">
                  Chính sách bảo mật
                </Link>
              </li>
              <li>
                <Link to="/term-of-services" className="text-slate-400 hover:text-cyan-400 transition-colors" data-testid="footer-link-terms">
                  Điều khoản sử dụng
                </Link>
              </li>
              <li>
                <Link to="/disclaimer" className="text-slate-400 hover:text-cyan-400 transition-colors" data-testid="footer-link-disclaimer">
                  Miễn trừ trách nhiệm
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-slate-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} {COMPANY.name}. Tất cả quyền được bảo lưu.
            </p>
            <p className="text-slate-500 text-sm">
              {COMPANY.address}
            </p>
          </div>
          <div className="text-slate-500 text-sm space-y-1">
            <p>Người đại diện: CAO MINH HIẾU</p>
            <p>Mã số hộ kinh doanh: 089090015053</p>
            <p>Nơi cấp: Phòng Kinh Tế, Hạ Tầng và Đô Thị Phường Long Xuyên</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Floating Widgets
const FloatingWidgets = () => {
  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-4" data-testid="floating-widgets">
      {/* Facebook Button */}
      <a
        href="https://byvn.net/9qn1"
        target="_blank"
        rel="noopener noreferrer"
        className="relative w-14 h-14 bg-blue-600 rounded-full shadow-lg flex items-center justify-center text-white hover:scale-110 transition-transform cursor-pointer group"
        data-testid="facebook-widget-button"
        aria-label="Chat Facebook"
      >
        <Facebook className="w-7 h-7" />
        <span className="absolute right-full mr-3 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          Chat Facebook
        </span>
      </a>

      {/* Zalo Button */}
      <a
        href={`https://zalo.me/${COMPANY.zalo}`}
        target="_blank"
        rel="noopener noreferrer"
        className="relative w-14 h-14 bg-blue-600 rounded-full shadow-lg flex items-center justify-center text-white hover:scale-110 transition-transform cursor-pointer group"
        data-testid="zalo-widget-button"
        aria-label="Chat Zalo"
      >
        <img 
          src="/zalo.svg" 
          alt="Zalo" 
          className="w-7 h-7"
        />
        <span className="absolute right-full mr-3 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          Chat Zalo
        </span>
      </a>

      {/* Phone Button */}
      <a
        href={`tel:${COMPANY.phone}`}
        onClick={() => gtag_report_conversion(`tel:${COMPANY.phone}`)}
        className="relative w-14 h-14 bg-red-600 rounded-full shadow-lg flex items-center justify-center text-white hover:scale-110 transition-transform cursor-pointer group"
        data-testid="phone-widget-button"
        aria-label="Gọi điện"
      >
        <div className="absolute inset-0 bg-red-600 rounded-full animate-pulse-ring"></div>
        <Phone className="w-7 h-7 relative z-10" />
        <span className="absolute right-full mr-3 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          {COMPANY.phone}
        </span>
      </a>
    </div>
  );
};

// Home Page
const HomePage = () => {
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <HeroSection />
      <ServicesSection />
      <PricingSection />
      <ContactSection />
      
      {/* Location Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-center mb-4 gap-2">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 whitespace-nowrap">TÌM THẤY XE GẦN BẠN</h3>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Car className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-gray-600 mb-6 text-center">
              Tài xế có thể đón bạn trong 5 phút
            </p>
            <div className="flex flex-col gap-3">
              <a
                href={`tel:${COMPANY.phone}`}
                onClick={(e) => {
                  e.preventDefault();
                  if (typeof gtag_report_conversion === 'function') {
                    gtag_report_conversion(`tel:${COMPANY.phone}`);
                  } else {
                    window.location.href = `tel:${COMPANY.phone}`;
                  }
                }}
                className="w-full bg-red-500 text-white py-3 px-4 rounded-xl font-semibold hover:bg-red-600 transition-colors text-center flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Gọi Ngay: {COMPANY.phone.replace(/(\d{4})(\d{4})(\d{2})/, "$1 $2 $3")}
              </a>
              <button
                onClick={() => setShowPopup(false)}
                className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white pt-32 pb-20" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-8">
          Chính sách bảo mật
        </h1>
        
        <div className="prose prose-slate prose-lg max-w-none">
          <p className="text-slate-600 mb-6">
            Cập nhật lần cuối: {new Date().toLocaleDateString('vi-VN')}
          </p>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            1. Thu thập thông tin
          </h2>
          <p className="text-slate-600 mb-4">
            {COMPANY.name} thu thập các thông tin sau khi bạn sử dụng dịch vụ của chúng tôi:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
            <li>Họ và tên</li>
            <li>Số điện thoại liên hệ</li>
            <li>Địa chỉ email (nếu cung cấp)</li>
            <li>Điểm đón và điểm đến</li>
            <li>Thông tin về yêu cầu đặt xe</li>
          </ul>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            2. Mục đích sử dụng thông tin
          </h2>
          <p className="text-slate-600 mb-4">
            Chúng tôi sử dụng thông tin của bạn cho các mục đích:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
            <li>Cung cấp dịch vụ vận chuyển theo yêu cầu</li>
            <li>Liên hệ xác nhận và hỗ trợ đặt xe</li>
            <li>Cải thiện chất lượng dịch vụ</li>
            <li>Gửi thông tin khuyến mãi (nếu bạn đồng ý)</li>
          </ul>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            3. Bảo mật thông tin
          </h2>
          <p className="text-slate-600 mb-6">
            Chúng tôi cam kết bảo mật thông tin cá nhân của bạn. Thông tin của bạn chỉ được sử dụng 
            cho mục đích cung cấp dịch vụ và không được chia sẻ cho bên thứ ba mà không có sự đồng ý của bạn.
          </p>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            4. Liên hệ
          </h2>
          <p className="text-slate-600 mb-4">
            Nếu bạn có bất kỳ câu hỏi nào về chính sách bảo mật, vui lòng liên hệ:
          </p>
          <ul className="list-none text-slate-600 space-y-2">
            <li><strong>Điện thoại:</strong> {COMPANY.phone}</li>
            <li><strong>Email:</strong> {COMPANY.email}</li>
            <li><strong>Địa chỉ:</strong> {COMPANY.address}</li>
          </ul>
        </div>

        <div className="mt-12">
          <Link to="/">
            <Button variant="outline" className="rounded-full" data-testid="back-home-button">
              Quay về trang chủ
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Terms of Service Page
const TermsOfServicePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white pt-32 pb-20" data-testid="terms-of-service-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-8">
          Điều khoản sử dụng
        </h1>
        
        <div className="prose prose-slate prose-lg max-w-none">
          <p className="text-slate-600 mb-6">
            Cập nhật lần cuối: {new Date().toLocaleDateString('vi-VN')}
          </p>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            1. Điều khoản chung
          </h2>
          <p className="text-slate-600 mb-6">
            Bằng việc sử dụng dịch vụ của {COMPANY.name}, bạn đồng ý tuân thủ và chịu ràng buộc 
            bởi các điều khoản và điều kiện sau đây. Nếu bạn không đồng ý với bất kỳ phần nào của 
            các điều khoản này, bạn không nên sử dụng dịch vụ của chúng tôi.
          </p>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            2. Dịch vụ
          </h2>
          <p className="text-slate-600 mb-4">
            {COMPANY.name} cung cấp các dịch vụ vận chuyển hành khách bao gồm:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
            <li>Taxi điện nội thành Long Xuyên</li>
            <li>Đưa đón sân bay</li>
            <li>Xe du lịch đường dài</li>
            <li>Xe cưới hỏi</li>
            <li>Xe hợp đồng theo tour</li>
          </ul>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            3. Đặt xe và thanh toán
          </h2>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
            <li>Khách hàng đặt xe qua điện thoại hoặc Zalo</li>
            <li>Giá cước được thông báo trước khi khách hàng sử dụng dịch vụ</li>
            <li>Thanh toán bằng tiền mặt hoặc chuyển khoản</li>
            <li>Giá có thể thay đổi tùy theo điều kiện thực tế</li>
          </ul>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            4. Trách nhiệm của khách hàng
          </h2>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
            <li>Cung cấp thông tin chính xác khi đặt xe</li>
            <li>Có mặt đúng giờ tại điểm đón</li>
            <li>Thanh toán đầy đủ theo giá đã thỏa thuận</li>
            <li>Tuân thủ quy định an toàn trên xe</li>
          </ul>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            5. Hủy chuyến
          </h2>
          <p className="text-slate-600 mb-6">
            Khách hàng có thể hủy chuyến miễn phí trước giờ đón 30 phút. Sau thời gian này, 
            có thể áp dụng phí hủy chuyến tùy theo từng trường hợp cụ thể.
          </p>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            6. Liên hệ
          </h2>
          <p className="text-slate-600 mb-4">
            Mọi thắc mắc về điều khoản sử dụng, vui lòng liên hệ:
          </p>
          <ul className="list-none text-slate-600 space-y-2">
            <li><strong>Điện thoại:</strong> {COMPANY.phone}</li>
            <li><strong>Email:</strong> {COMPANY.email}</li>
          </ul>
        </div>

        <div className="mt-12">
          <Link to="/">
            <Button variant="outline" className="rounded-full" data-testid="back-home-button">
              Quay về trang chủ
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Disclaimer Page
const DisclaimerPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white pt-32 pb-20" data-testid="disclaimer-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-8">
          Tuyên bố miễn trừ trách nhiệm
        </h1>
        
        <div className="prose prose-slate prose-lg max-w-none">
          <p className="text-slate-600 mb-6">
            Cập nhật lần cuối: {new Date().toLocaleDateString('vi-VN')}
          </p>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            1. Giới hạn trách nhiệm
          </h2>
          <p className="text-slate-600 mb-6">
            {COMPANY.name} nỗ lực cung cấp dịch vụ vận chuyển an toàn và chất lượng. Tuy nhiên, 
            chúng tôi không chịu trách nhiệm về:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
            <li>Thiệt hại do sự kiện bất khả kháng (thiên tai, tai nạn giao thông do bên thứ ba gây ra, v.v.)</li>
            <li>Chậm trễ do tình trạng giao thông, thời tiết hoặc các yếu tố ngoài tầm kiểm soát</li>
            <li>Mất mát tài sản cá nhân do khách hàng để quên trên xe (mặc dù chúng tôi sẽ cố gắng tìm và trả lại)</li>
          </ul>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            2. Bảo hiểm
          </h2>
          <p className="text-slate-600 mb-6">
            Tất cả các phương tiện của {COMPANY.name} đều được mua bảo hiểm trách nhiệm dân sự theo quy định của pháp luật. 
            Trong trường hợp xảy ra sự cố, quyền lợi của khách hàng sẽ được giải quyết theo quy định của bảo hiểm.
          </p>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            3. Thông tin trên website
          </h2>
          <p className="text-slate-600 mb-6">
            Thông tin trên website này chỉ mang tính chất tham khảo. Giá cước và chi tiết dịch vụ có thể 
            thay đổi mà không cần thông báo trước. Vui lòng liên hệ trực tiếp để được báo giá chính xác.
          </p>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-8 mb-4">
            4. Khiếu nại
          </h2>
          <p className="text-slate-600 mb-4">
            Mọi khiếu nại về dịch vụ vui lòng gửi về:
          </p>
          <ul className="list-none text-slate-600 space-y-2">
            <li><strong>Điện thoại:</strong> {COMPANY.phone}</li>
            <li><strong>Email:</strong> {COMPANY.email}</li>
            <li><strong>Địa chỉ:</strong> {COMPANY.address}</li>
          </ul>
          <p className="text-slate-600 mt-4">
            Chúng tôi cam kết phản hồi trong vòng 24 giờ làm việc.
          </p>
        </div>

        <div className="mt-12">
          <Link to="/">
            <Button variant="outline" className="rounded-full" data-testid="back-home-button">
              Quay về trang chủ
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App font-body">
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/policy-privacy" element={<PrivacyPolicyPage />} />
          <Route path="/term-of-services" element={<TermsOfServicePage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
        </Routes>
        <Footer />
        <FloatingWidgets />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
